/**
 * PATH: apps/quote/src/pages/import-quote/ImportReviewTable.tsx
 * PURPOSE: Step 2 of AI quote import — review/edit the AI-parsed lines and the
 *          first-principles labour/material/plant/subcon cost split before commit.
 */
import { useMemo } from 'react'
import { Info } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { ImportPreview, ImportLineItem } from '@/lib/api/quote-import-api'

interface Props {
  preview: ImportPreview
  onChange: (next: ImportPreview) => void
}

const COST_FIELDS = ['labour_cost', 'material_cost', 'plant_cost', 'subcon_cost'] as const
const COST_LABELS: Record<string, string> = {
  labour_cost: 'Labour', material_cost: 'Materials', plant_cost: 'Plant', subcon_cost: 'Subcon',
}

function num(v: string | number | undefined): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '0'))
  return Number.isFinite(n) ? n : 0
}

function confColor(c: number): string {
  if (c >= 0.7) return 'bg-green-500'
  if (c >= 0.5) return 'bg-amber-500'
  return 'bg-red-500'
}

export function ImportReviewTable({ preview, onChange }: Props) {
  const totals = useMemo(() => {
    const t = { labour: 0, material: 0, plant: 0, subcon: 0, cost: 0, sell: 0 }
    for (const s of preview.sections) {
      for (const it of s.items) {
        t.labour += num(it._split.labour_cost)
        t.material += num(it._split.material_cost)
        t.plant += num(it._split.plant_cost)
        t.subcon += num(it._split.subcon_cost)
        t.sell += num(it._split.chargeout_total)
      }
    }
    t.cost = t.labour + t.material + t.plant + t.subcon
    return t
  }, [preview])

  const editItem = (si: number, ii: number, patch: Partial<ImportLineItem> & { split?: Record<string, string> }) => {
    const next: ImportPreview = JSON.parse(JSON.stringify(preview))
    const item = next.sections[si].items[ii]
    if (patch.description !== undefined) item.description = patch.description
    if (patch.split) {
      Object.assign(item._split, patch.split)
      item._split.cost_total = String(
        COST_FIELDS.reduce((acc, f) => acc + num(item._split[f]), 0).toFixed(2))
    }
    onChange(next)
  }

  return (
    <div className="space-y-4">
      {preview.warnings?.length > 0 && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
          <ul className="list-disc pl-5 space-y-0.5">
            {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Description</th>
              <th className="px-2 py-2 text-right font-medium">Qty</th>
              {COST_FIELDS.map((f) => <th key={f} className="px-2 py-2 text-right font-medium">{COST_LABELS[f]} cost</th>)}
              <th className="px-2 py-2 text-right font-medium">Sell</th>
              <th className="px-2 py-2 text-center font-medium">Conf.</th>
            </tr>
          </thead>
          <tbody>
            {preview.sections.map((section, si) => (
              <>
                <tr key={`s-${si}`} className="bg-gray-100 dark:bg-gray-800/60">
                  <td colSpan={8} className="px-3 py-1.5 font-semibold text-gray-700 dark:text-gray-200">{section.title}</td>
                </tr>
                {section.items.map((it, ii) => (
                  <tr key={`${si}-${ii}`} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="px-3 py-1.5">
                      <input
                        value={it.description}
                        onChange={(e) => editItem(si, ii, { description: e.target.value })}
                        className="w-full bg-transparent focus:bg-white dark:focus:bg-gray-900 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                      />
                      {it._split.basis && (
                        <span className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5" title={it._split.basis}>
                          <Info className="h-3 w-3" /> {it._split.basis.slice(0, 70)}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right text-gray-500">{num(it.quantity)} {it.unit}</td>
                    {COST_FIELDS.map((f) => (
                      <td key={f} className="px-2 py-1.5 text-right">
                        <input
                          type="number" step="0.01"
                          value={num(it._split[f])}
                          onChange={(e) => editItem(si, ii, { split: { [f]: e.target.value } })}
                          className="w-20 text-right bg-transparent focus:bg-white dark:focus:bg-gray-900 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right text-gray-600 dark:text-gray-300">{formatCurrency(num(it._split.chargeout_total))}</td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={cn('inline-block h-2.5 w-2.5 rounded-full', confColor(it._split.confidence))}
                        title={`${Math.round((it._split.confidence || 0) * 100)}% confidence`} />
                    </td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 dark:bg-gray-800 font-semibold">
            <tr className="border-t-2 border-gray-200 dark:border-gray-700">
              <td className="px-3 py-2">Totals</td>
              <td />
              <td className="px-2 py-2 text-right">{formatCurrency(totals.labour)}</td>
              <td className="px-2 py-2 text-right">{formatCurrency(totals.material)}</td>
              <td className="px-2 py-2 text-right">{formatCurrency(totals.plant)}</td>
              <td className="px-2 py-2 text-right">{formatCurrency(totals.subcon)}</td>
              <td className="px-2 py-2 text-right">{formatCurrency(totals.sell)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Total estimated cost <span className="font-semibold">{formatCurrency(totals.cost)}</span> ·
        sell <span className="font-semibold">{formatCurrency(totals.sell)}</span> ·
        margin <span className="font-semibold">{totals.sell > 0 ? Math.round((1 - totals.cost / totals.sell) * 100) : 0}%</span>.
        Edit any cost to refine the first-principles split before saving.
      </p>
    </div>
  )
}
